import { createContext, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@marine/ui/button';
import type { BoatFormData, NotImportedOptionLine, ProvenanceEntry } from '@/modules/inventory/types/boat.types';
import type { ScalarValue } from '@/modules/inventory/utils/boat-changes';
import { formatChangeValue } from '@/modules/inventory/utils/boat-change-labels';
import { sameValue } from '@/modules/inventory/utils/boat-changes';
import { reviewReasonLabel } from '@/modules/inventory/utils/review-reasons';
import { CopilotSessionContext } from './CopilotSessionContext';
import type { FeedItem } from './hooks/useCopilotSession';

/**
 * #1178 (spec §2) — what the canvas knows about a field beyond its value:
 *  - `serverValues`: fields the dealer changed that the merge (or another tab)
 *    also changed; the dealer's value stays and the saved one is offered;
 *  - review flags on the field's provenance (#1179 photo suggestions and
 *    conflicts, #1188 HIN/VIN doubts), decided here through the suggested-updates
 *    endpoints (`acceptSuggestion`, `dismissFlag`).
 */
export interface CanvasReviewValue {
  serverValues: Record<string, ScalarValue>;
  takeServerValue: (field: string) => void;
  keepMine: (field: string) => void;
  /** "Use it": the dealer chooses the suggested `value` for `field`. */
  acceptSuggestion?: (field: string, fieldSourceId: string, value: unknown) => void;
  dismissFlag?: (fieldSourceIds: string[]) => void;
  /**
   * #1317 — the dealer picked `entry` in the field's ConflictChooser (value already
   * written); `restore` undoes the pick when the server does not confirm it.
   */
  pickSource?: (field: string, entry: ProvenanceEntry, restore?: () => void) => void;
  /** Flags the dealer has decided; not shown again while the field still reports them. */
  hiddenFlagIds?: ReadonlySet<string>;
  /** The image a photo conflict was checked against. */
  photoUrl?: (mediaId: number | string) => string | null;
  /** #1182 — option lines the merge skipped (the equipment section's "Not imported" list). */
  notImportedOptions?: NotImportedOptionLine[];
}

export const CanvasReviewContext = createContext<CanvasReviewValue | null>(null);

/** Feed-finding field names (extractor core keys) that differ from the form's. #1337: the set field is `boat_types`. */
const FINDING_FIELDS: Record<string, string> = { hin: 'hin_number', boat_type: 'boat_types' };

/** The latest photo-conflict finding for a form field (the feed is seq-ordered). */
function photoFinding(
  feed: FeedItem[] | undefined,
  fieldName: string,
): { observed: string | null; photoMediaId: number | string | null } | null {
  let found: { observed: string | null; photoMediaId: number | string | null } | null = null;
  for (const item of feed ?? []) {
    const detail = item.payload?.detail as Record<string, unknown> | undefined;
    if (!detail || detail.review_reason !== 'photo_conflict' || typeof detail.field !== 'string') continue;
    if ((FINDING_FIELDS[detail.field] ?? detail.field) !== fieldName) continue;
    const media = detail.photo_media_id;
    found = {
      observed: typeof detail.observed === 'string' ? detail.observed : null,
      photoMediaId: typeof media === 'number' || typeof media === 'string' ? media : null,
    };
  }
  return found;
}

const NOTICE_CLASS =
  'flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-[0.8125rem]';

/** Notices under a canvas field's label. Renders nothing outside the canvas. */
export function FieldNotices({ fieldName }: { fieldName: string }) {
  const review = useContext(CanvasReviewContext);
  const session = useContext(CopilotSessionContext);
  const form = useFormContext<BoatFormData>();
  if (!review) return null;

  const raw = form
    ? (form.watch(`_provenance.${fieldName}` as const) as ProvenanceEntry | ProvenanceEntry[] | undefined)
    : undefined;
  const entries = (Array.isArray(raw) ? raw : raw ? [raw] : []).filter(
    (entry) => entry.needsReview && entry.reviewReason && entry.id && !review.hiddenFlagIds?.has(entry.id),
  );
  const byReason = new Map<string, ProvenanceEntry[]>();
  for (const entry of entries) {
    const reason = entry.reviewReason as string;
    byReason.set(reason, [...(byReason.get(reason) ?? []), entry]);
  }

  // #1317 — numeric-aware: a saved "24.50" is the form's 24.5, not a disagreement.
  const hasServerValue =
    fieldName in review.serverValues &&
    !(form && sameValue(form.watch(fieldName as never), review.serverValues[fieldName]));
  if (!hasServerValue && byReason.size === 0) return null;

  return (
    <>
      {hasServerValue && (
        <div role="status" data-testid={`server-value-${fieldName}`} className={NOTICE_CLASS}>
          <span className="min-w-0">
            The saved listing has{' '}
            <span className="font-semibold">{formatChangeValue(review.serverValues[fieldName])}</span>
          </span>
          <span className="flex shrink-0 gap-1">
            <Button type="button" size="sm" variant="outline" onClick={() => review.takeServerValue(fieldName)}>
              Use saved value
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => review.keepMine(fieldName)}>
              Keep mine
            </Button>
          </span>
        </div>
      )}

      {[...byReason.entries()].map(([reason, group]) => {
        const ids = group.map((entry) => entry.id as string);
        const testId = `review-${fieldName}-${reason}`;

        if (reason === 'photo_suggestion' || reason === 'spec_lookup') {
          // #1179 photo / #1307 catalogue: one suggested value the dealer uses or dismisses.
          const lead = reason === 'spec_lookup' ? 'The catalogue lists' : 'A photo suggests';
          return (
            <div key={reason} role="status" data-testid={testId} className={NOTICE_CLASS}>
              <span className="min-w-0">
                {lead} <span className="font-semibold">{formatChangeValue(group[0].value)}</span>.
              </span>
              <span className="flex shrink-0 gap-1">
                <Button type="button" size="sm" variant="outline" onClick={() => review.acceptSuggestion?.(fieldName, ids[0], group[0].value)}>
                  Use it
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => review.dismissFlag?.(ids)}>
                  Dismiss
                </Button>
              </span>
            </div>
          );
        }

        const finding = reason === 'photo_conflict' ? photoFinding(session?.feed, fieldName) : null;
        const photo = finding?.photoMediaId != null ? (review.photoUrl?.(finding.photoMediaId) ?? null) : null;
        const text = reason === 'photo_conflict'
          ? `The photos show ${finding?.observed ?? 'something different'}.`
          : (reviewReasonLabel(reason) ?? 'Check this value.');

        return (
          <div key={reason} role="status" data-testid={testId} className={NOTICE_CLASS}>
            <span className="flex min-w-0 items-center gap-2">
              {photo && (
                <img src={photo} alt="The photo it was checked against" className="size-10 shrink-0 rounded object-cover" />
              )}
              <span>{text}</span>
            </span>
            <Button type="button" size="sm" variant="outline" onClick={() => review.dismissFlag?.(ids)}>
              Keep this value
            </Button>
          </div>
        );
      })}
    </>
  );
}
